import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../app/hooks';
import type { Moment } from 'moment';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { TextField } from '@mui/material';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import type { TimeValidationError } from '@mui/x-date-pickers/models';
import bumpToMinDate from '../utils/bumpToMinDate';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import getThemeLookup from '../selectors/getThemeLookup';
import combineStyles from '../utils/combineStyles';
import { TEXT_PROPERTIES, SELECT_PROPERTIES } from '../utils/theme';

const allowedMinuteIntervals = [1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30] as const;

function roundToNearestInterval(minuteInterval: number): number {
  return (
    allowedMinuteIntervals.find(interval => interval === minuteInterval) ||
    allowedMinuteIntervals[0]
  );
}

function roundToNearestFiveMinutes(momentObject: Moment): Moment {
  const minutes = momentObject.minutes();
  const roundedMinutes = Math.ceil(minutes / 5) * 5;

  // Adjust for wrapping around the hour (e.g., 58 minutes rounded to 60)
  if (roundedMinutes === 60) {
    return momentObject.add(1, 'hour').minutes(0).seconds(0).milliseconds(0);
  } else {
    return momentObject.minutes(roundedMinutes).seconds(0).milliseconds(0);
  }
}

// ! Do not include any specific functionality for a specific flow, this is a primative
// ! component. We should be able to use this component in any circumstances as building blocks.

/**
 * # TimeSelector
 * This is a primitive component for a time (only time) selector.
 *
 * @param closePicker
 * @returns
 */
const TimeSelector = ({
  value,
  setTime,
  earliestSelectableTime,
  latestSelectableDate,
  minuteInterval,
}: {
  value?: string;
  setTime?: (time: string) => void;
  earliestSelectableTime: string;
  latestSelectableDate: string;
  minuteInterval: number;
}) => {
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const isMobile = useAppSelector(getDeviceTypeMobile);

  /**
   * Error helper text
   */
  const [error, setError] = useState<TimeValidationError | null>(null);

  const errorMessage = useMemo(() => {
    switch (error) {
      case 'maxTime':
      case 'minTime': {
        return t('timeErrorForLocation');
      }

      default: {
        return '';
      }
    }
  }, [error]);

  /**
   * Output:
   */

  const minTime = moment(earliestSelectableTime);
  const maxTime = moment(latestSelectableDate);

  const minTimeAsFallback = roundToNearestFiveMinutes(
    minTime.clone().add(1, 'minute'),
  ).toISOString();
  const maxTimeAsFallback = roundToNearestFiveMinutes(
    maxTime.clone().subtract(5, 'minute'),
  ).toISOString();

  const [tempValue, setTempValue] = useState(value);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const tempValueMoment = tempValue ? moment(tempValue) : null;

  const onOpen = () => {
    if (!value) {
      setTempValue(minTimeAsFallback);
    }

    if (value && !tempValue) {
      if (moment(value) <= minTime) {
        setTempValue(minTimeAsFallback);
      } else {
        setTempValue(value);
      }
    }

    setError(null);
  };

  const onChange = (newValue: Moment | null) => {
    setError(null);

    if (setTime) {
      if (!newValue) {
        // If no value is selected, we do not change the time.
        return;
      }

      if (newValue > maxTime) {
        // If selected date is after the latest selectable date
        // fall back to the latest selectable date.
        setTempValue(maxTimeAsFallback);
      } else if (newValue <= minTime) {
        // If selected date is earlier than the earliest selectable date
        // fall back to the earliest selectable date.
        setTempValue(minTimeAsFallback);
      } else {
        setTempValue(
          moment(value)
            .set({
              hours: newValue.hours(),
              minutes: newValue.minutes(),
            })
            .toISOString(),
        );
      }
    }
  };

  const onAccept = () => {
    if (setTime && tempValue) {
      setTime(tempValue);
    }
  };

  const onError = (newError: TimeValidationError) => {
    if (newError !== null) {
      setError(newError);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MobileTimePicker
        /**
         * When there is no value the label will show instead of the placeholder. We
         * use this ternary to ensure the placeholder shows which makes for better UX.
         */
        label={tempValueMoment ? t('field.dateTime.time') : undefined}
        value={tempValueMoment}
        sx={{
          maxWidth: 230,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: p('fieldDefaultBorder', ['color']).color,
            },
            '&:hover fieldset': {
              borderColor: p('fieldHoverBorderColor', ['color']).color,
            },
            '&.Mui-focused fieldset': {
              borderColor: p('fieldBorderFocus', ['color']).color,
            },
          },
        }}
        onOpen={onOpen}
        /**
         * It's possible for minTime to be a future date but the timepicker might not have a reference
         * to that date. The time picker will then return a moment object with the current date.
         * In this situation any time the user selects will be wrong and the time will be bumped
         * to the start of the minTime day (which is usually midnight). bumpToMinDate will
         * adjust the selected date accordingly but still preserve the selected time.
         */
        onChange={data => onChange(bumpToMinDate({ data, minTime }))}
        onClose={onAccept}
        onError={onError}
        closeOnSelect={false}
        minutesStep={roundToNearestInterval(minuteInterval)}
        minTime={minTime}
        maxTime={maxTime}
        slotProps={{
          textField: {
            helperText: errorMessage,
            placeholder: t('field.dateTime.placeholder'),
            error: !tempValueMoment,
            FormHelperTextProps: {
              style: {
                color: p('timeSelectorErrorText', ['color']).color,
              },
            },
            InputProps: {
              style: p('formTextFieldInput', SELECT_PROPERTIES),
            },
            InputLabelProps: {
              style: p('dateTimeInputLabel', SELECT_PROPERTIES),
            },
          },
        }}
        slots={
          isMobile
            ? {
                textField: props => (
                  <TextField
                    {...props}
                    variant="standard"
                    sx={{
                      flex: 1,
                    }}
                    FormHelperTextProps={{
                      style: {
                        color: p('helperColor', ['color']).color,
                      },
                    }}
                    InputProps={{
                      style: combineStyles(
                        p('defaultText', TEXT_PROPERTIES),
                        p('input', SELECT_PROPERTIES),
                        p('formTextFieldInput', SELECT_PROPERTIES),
                      ),
                    }}
                    InputLabelProps={{
                      style: combineStyles(
                        p('defaultText', TEXT_PROPERTIES),
                        p('input', SELECT_PROPERTIES),
                        p('dateTimeInputLabel', SELECT_PROPERTIES),
                      ),
                    }}
                  />
                ),
              }
            : {}
        }
      />
    </LocalizationProvider>
  );
};

export default TimeSelector;
